import { Button } from "@hortis/ui/button";
import type { ReactElement } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { AddLocationFilter } from "./add-location-filter";
import { LocationFilterChipGroup } from "./filter-chip-group";
import { LocationFilterModals } from "./modals/filter-modals";
import type { LocationFilterType, LocationFiltersProps } from "./types";

export interface OpenFilterArgs<T> {
  filterType: T | false;
  anchorElRef?: HTMLElement | null;
  editFilterId?: string;
}

export const LocationFilters = ({
  filters,
  dispatchFilters,
  resetFilters,
  filterCount,
  hideClear,
}: Omit<LocationFiltersProps, "orderBy" | "updateOrderBy"> & {
  hideClear?: boolean;
}): ReactElement => {
  const [openFilter, setOpenFilter] = useState<false | LocationFilterType>(
    false,
  );
  const [editFilterId, setEditFilterId] = useState<string | null>(null);
  const filterModalAnchorEl = useRef<HTMLElement | null>(null);
  const editFilter = useMemo(
    () =>
      editFilterId == null
        ? undefined
        : filters.and.find(({ id }) => id === editFilterId),
    [filters, editFilterId],
  );

  const resetAllFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleOpenFilter = useCallback(
    ({
      filterType,
      anchorElRef,
      editFilterId,
    }: OpenFilterArgs<LocationFilterType>) => {
      setEditFilterId(editFilterId ?? null);
      if (anchorElRef != null) {
        filterModalAnchorEl.current = anchorElRef;
      }
      setOpenFilter(filterType);
    },
    [],
  );
  const handleFilterModalClose = useCallback(() => {
    setOpenFilter(false);
  }, []);

  return (
    <div className="flex flex-1 items-start justify-between gap-3">
      <div className="flex flex-1 flex-wrap items-center gap-3">
        <LocationFilterChipGroup
          handleOpenFilter={handleOpenFilter}
          filters={filters.and}
          dispatchFilter={dispatchFilters}
        />
        <AddLocationFilter setOpenFilter={handleOpenFilter} iconButton />
        <LocationFilterModals
          editFilter={editFilter}
          openFilter={openFilter}
          handleFilterModalClose={handleFilterModalClose}
          filterModalAnchorEl={filterModalAnchorEl}
          dispatchFilters={dispatchFilters}
        />
      </div>
      {hideClear !== true && (
        <div className="flex items-center gap-3">
          {filterCount > 1 && (
            <Button
              onClick={resetAllFilters}
              className="shrink-0"
              variant="tertiaryColor"
              size="sm"
            >
              Clear filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
